import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Pencil, Save, Sparkles, Package } from 'lucide-react';
import { supabase, withRetry } from '@/lib/supabase';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

interface ProductEditMenuProps {
  productId: string;
  currentName: string;
  currentUrl: string;
  currentPrice: number;
  currentCategory: string;
  onUpdate: () => void;
  onLocalUpdate?: (productId: string, updates: { name?: string; url?: string; price?: number; category?: string }) => void;
}

export const ProductEditMenu = ({ 
  productId, 
  currentName, 
  currentUrl, 
  currentPrice, 
  currentCategory,
  onUpdate, 
  onLocalUpdate 
}: ProductEditMenuProps) => {
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [name, setName] = useState(currentName);
  const [url, setUrl] = useState(currentUrl);
  const [price, setPrice] = useState(currentPrice.toString());
  const [category, setCategory] = useState(currentCategory);
  const [isLoading, setIsLoading] = useState(false);

  // Verfügbare Kategorien
  const categories = ['Business', 'Nice-to-Have', 'Persönlich', 'Sonstiges'];

  // Reset form when opening
  const handleOpen = () => {
    setName(currentName);
    setUrl(currentUrl);
    setPrice(currentPrice.toString());
    setCategory(currentCategory);
    setIsOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) return;

    setIsLoading(true);
    try {
      const updatedPrice = parseFloat(price.replace(',', '.'));
      
      if (isNaN(updatedPrice) || updatedPrice < 0) {
        toast.error('Bitte gib einen gültigen Preis ein');
        return;
      }

      // Verwende withRetry für robuste API-Aufrufe
      await withRetry(async () => {
        const { error } = await supabase
          .from('waitlist')
          .update({
            name: name.trim(),
            url: url.trim(),
            price: updatedPrice,
            category: category
          })
          .eq('id', productId)
          .eq('user_id', user.id);

        if (error) throw error;
        return true;
      });

      // Preis-Historie aktualisieren, falls sich der Preis geändert hat
      if (updatedPrice !== currentPrice) {
        try {
          await withRetry(async () => {
            const { error: priceHistoryError } = await supabase
              .from('price_history')
              .insert({
                product_id: productId,
                user_id: user.id,
                price: updatedPrice,
                tracked_at: new Date().toISOString()
              });

            if (priceHistoryError) throw priceHistoryError;
            return true;
          });
        } catch (priceHistoryError) {
          console.error('Error updating price history:', priceHistoryError);
          // Preis-Historie-Fehler sind nicht kritisch
        }
      }

      // Sofortige lokale Aktualisierung für bessere UX
      if (onLocalUpdate) {
        onLocalUpdate(productId, { 
          name: name.trim(), 
          url: url.trim(), 
          price: updatedPrice,
          category: category
        });
      }

      setIsOpen(false);
      onUpdate(); // Vollständige Aktualisierung der Liste
      toast.success('Produkt erfolgreich aktualisiert');
    } catch (error) {
      console.error('Error updating product:', error);
      toast.error('Fehler beim Aktualisieren des Produkts. Bitte versuchen Sie es erneut.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Button 
        variant="ghost" 
        size="sm" 
        onClick={handleOpen}
        className="hover:bg-emerald-50 hover:text-emerald-700 p-1 h-auto rounded-lg transition-all duration-300 hover:scale-110"
      >
        <Pencil className="h-4 w-4 text-gray-400 hover:text-emerald-600 transition-colors duration-300" />
      </Button>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="sm:max-w-[500px] bg-gradient-to-br from-white to-emerald-50/30 border-emerald-200/50">
          <DialogHeader className="text-center pb-6">
            <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
              <Package className="w-6 h-6 text-white" />
            </div>
            <DialogTitle className="text-xl text-emerald-900">Produkt bearbeiten</DialogTitle>
            <DialogDescription className="text-emerald-700">
              Aktualisiere die Details deines Produkts. Für Preisänderungen nutze den Preisverlauf.
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-emerald-800 font-medium">Produktname *</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="z.B. MacBook Pro 14"
                required
                className="border-emerald-200 focus:border-emerald-500 focus:ring-emerald-500"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="price" className="text-emerald-800 font-medium">Ursprungspreis (€) *</Label>
              <Input
                id="price"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                placeholder="2499,00"
                required
                inputMode="decimal"
                pattern="[0-9]*[,.]?[0-9]*"
                className="border-emerald-200 focus:border-emerald-500 focus:ring-emerald-500"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="category" className="text-emerald-800 font-medium">Kategorie *</Label>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger id="category" className="border-emerald-200 focus:border-emerald-500 focus:ring-emerald-500">
                  {category ? (
                    <div className="flex items-center gap-2">
                      {category === 'Business' && <span>💼</span>}
                      {category === 'Nice-to-Have' && <span>🎯</span>}
                      {category === 'Persönlich' && <span>🏠</span>}
                      {category === 'Sonstiges' && <span>📦</span>}
                      <span>{category}</span>
                    </div>
                  ) : (
                    <span className="text-muted-foreground">Wähle eine Kategorie</span>
                  )}
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Business">
                    <div className="flex items-center gap-2">
                      <span>💼</span>
                      <span>Business</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="Nice-to-Have">
                    <div className="flex items-center gap-2">
                      <span>🎯</span>
                      <span>Nice-to-Have</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="Persönlich">
                    <div className="flex items-center gap-2">
                      <span>🏠</span>
                      <span>Persönlich</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="Sonstiges">
                    <div className="flex items-center gap-2">
                      <span>📦</span>
                      <span>Sonstiges</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="url" className="text-emerald-800 font-medium">Produkt-URL (optional)</Label>
              <Input
                id="url"
                type="url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://..."
                className="border-emerald-200 focus:border-emerald-500 focus:ring-emerald-500"
              />
            </div>

            <div className="bg-emerald-50 p-4 rounded-lg border border-emerald-200">
              <p className="text-emerald-800 text-sm text-center">
                💡 <strong>Tipp:</strong> Preisänderungen werden automatisch in deiner Historie gespeichert!
              </p>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setIsOpen(false)}
                className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                disabled={isLoading}
              >
                Abbrechen
              </Button>
              <Button 
                type="submit"
                disabled={isLoading}
                className="bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
              >
                {isLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    Wird gespeichert...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Änderungen speichern
                  </>
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}; 